import { createTheme, type ThemeOptions } from "@mui/material/styles";
// Import type augmentation for theme.vars CSS variables
import type {} from "@mui/material/themeCssVarsAugmentation";
import labelStyles from "./figma-label-styles";
import figmaTheme from "./figma-theme";
import themeOverrides from "./figma-theme-overrides";

// Extend MUI Theme to include custom properties
declare module "@mui/material/styles" {
  interface TypeBackground {
    contentBackground: string;
    default: string;
    "paper-elevation-0": string;
    "paper-elevation-1": string;
    "paper-elevation-2": string;
    "paper-elevation-3": string;
    "paper-elevation-4": string;
    "paper-elevation-5": string;
    "paper-elevation-6": string;
    "paper-elevation-7": string;
    "paper-elevation-8": string;
    "paper-elevation-9": string;
    "paper-elevation-10": string;
    "paper-elevation-11": string;
    "paper-elevation-12": string;
    "paper-elevation-13": string;
    "paper-elevation-14": string;
    "paper-elevation-15": string;
    "paper-elevation-16": string;
    "paper-elevation-17": string;
    "paper-elevation-18": string;
    "paper-elevation-19": string;
    "paper-elevation-20": string;
    "paper-elevation-21": string;
    "paper-elevation-22": string;
    "paper-elevation-23": string;
    "paper-elevation-24": string;
  }

  interface PaletteColor {
    _states: {
      hover?: string;
      selected?: string;
      focus?: string;
      focusVisible?: string;
      outlinedBorder?: string;
    };
  }

  interface SimplePaletteColorOptions {
    _states?: {
      hover?: string;
      selected?: string;
      focus?: string;
      focusVisible?: string;
      outlinedBorder?: string;
    };
  }

  interface PaletteOptions {
    _components?: {
      switch?: {
        knobFillEnabled?: string;
        slideFill?: string;
      };
      alert?: {
        error?: {
          color?: string;
          background?: string;
        };
        warning?: {
          color?: string;
          background?: string;
        };
        info?: {
          color?: string;
          background?: string;
        };
        success?: {
          color?: string;
          background?: string;
        };
      };
      avatar?: {
        fill?: string;
      };
      appBar?: {
        defaultFill?: string;
      };
    };
  }

  interface Palette {
    _components: {
      switch: {
        knobFillEnabled: string;
        slideFill: string;
      };
      alert: {
        error: {
          color: string;
          background: string;
        };
        warning: {
          color: string;
          background: string;
        };
        info: {
          color: string;
          background: string;
        };
        success: {
          color: string;
          background: string;
        };
      };
      avatar: {
        fill: string;
      };
      appBar: {
        defaultFill: string;
      };
    };
  }

  interface TypographyVariants {
    label1: React.CSSProperties;
    label2: React.CSSProperties;
    label3: React.CSSProperties;
    label4: React.CSSProperties;
    fontWeightSemiBold: number;
  }

  interface TypographyVariantsOptions {
    label1?: React.CSSProperties;
    label2?: React.CSSProperties;
    label3?: React.CSSProperties;
    label4?: React.CSSProperties;
    fontWeightSemiBold?: number;
  }

  interface Theme {
    customFontSizes?: {
      10: string;
      12: string;
      13: string;
      14: string;
      15: string;
      16: string;
      20: string;
      24: string;
      28: string;
      32: string;
      40: string;
      68: string;
    };
  }

  interface ThemeOptions {
    customFontSizes?: {
      10?: string;
      12?: string;
      13?: string;
      14?: string;
      15?: string;
      16?: string;
      20?: string;
      24?: string;
      28?: string;
      32?: string;
      40?: string;
      68?: string;
    };
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    label1: true;
    label2: true;
    label3: true;
    label4: true;
  }
}

// Create the Figma theme with color schemes for dark mode support
export const figma = createTheme({
  ...figmaTheme,
  cssVariables: {
    colorSchemeSelector: "data",
  },
  defaultColorScheme: "light",
  colorSchemes: figmaTheme.colorSchemes as ThemeOptions["colorSchemes"],
  shadows: figmaTheme.shadows as ThemeOptions["shadows"],
  typography: {
    ...figmaTheme.typography,
    ...themeOverrides.typography,
    ...labelStyles.typography,
  },
  customFontSizes: themeOverrides.customFontSizes,
  components: figmaTheme.components as ThemeOptions["components"],
});
